import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Copy, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import styles from './WidgetList.module.css';
import { useWidgetsStore } from '../../store/widgetsStore';
import { useAuthStore } from '../../../../store/authStore';
import { UserRole } from '../../../../enums/userRole';
import { toast } from '../../../../lib/toast';
import InputModal from '../../../../ui/inputModal/InputModal';
import type { Widget } from '../../store/widgetsStore';
import { hasMinRole } from '../../../../lib/permissions';

export default function WidgetList() {
  const navigate = useNavigate();
  const { widgets, loading, fetchWidgets, toggleWidget, deleteWidget, duplicateWidget } = useWidgetsStore();
  const user = useAuthStore(state => state.user);
  const isSuperAdmin = user?.role?.id === UserRole.SuperAdmin;
  const canManage = hasMinRole(user?.role?.id, UserRole.WorkzoneManager);
  const canDelete = hasMinRole(user?.role?.id, UserRole.Admin);
  const [deleteTarget, setDeleteTarget] = useState<Widget | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<Widget | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  // Admin users see only active widgets
  const displayWidgets = isSuperAdmin ? widgets : widgets.filter(w => w.isActive);

  useEffect(() => {
    const canView = hasMinRole(user?.role?.id, UserRole.WorkzoneManager) || user?.role?.id === UserRole.PromptEditor;
    if (!user || !canView) {
      navigate('/');
    }
  }, [user])

  useEffect(() => {
    // Widgets already loaded by Layout; re-fetch for SuperAdmin, silent for Admin
    fetchWidgets({ silent: !isSuperAdmin });
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    try {
      setDeleting(true);
      await deleteWidget(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmDuplicate = async (newName?: string) => {
    if (!duplicateTarget || !newName || duplicating) return;
    try {
      setDuplicating(true);
      await duplicateWidget(duplicateTarget.id, newName);
      toast.success('AI agent duplicated successfully');
    } catch (error: any) {
      toast.error(`Failed to duplicate AI agent: ${error.message}`);
    } finally {
      setDuplicating(false);
      setDuplicateTarget(null);
    }
  };

  const getPortalUrl = (widget: Widget) => `${window.location.origin}/portal/${widget.id}`;

  const copyPortalLink = async (widget: Widget) => {
    try {
      await navigator.clipboard.writeText(getPortalUrl(widget));
      toast.success('Portal link copied to clipboard');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (loading) return <div className={styles.loading}>Loading AI agents...</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Chat AI Settings</h1>
          <p>Manage your AI agent configurations</p>
        </div>
        <div className={styles.headerActions}>
          {canManage && (
            <button className={styles.createBtn} onClick={() => navigate('/widgets/new')}>
              + Create New AI Agent
            </button>
          )}
        </div>
      </div>

      {displayWidgets.length === 0 ? (
        <div className={styles.empty}>
          <h2>{canManage ? 'No AI agents created yet' : 'No active AI agents'}</h2>
          <p>{canManage ? 'Create your first AI agent to get started.' : 'There are no active AI agents available.'}</p>
          {canManage && (
            <button className={styles.createBtn} onClick={() => navigate('/widgets/new')}>
              Create Your First AI Agent
            </button>
          )}
        </div>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <div>Name</div>
            <div>Status</div>
            <div>Portal Link</div>
            <div>Created</div>
            <div>Actions</div>
          </div>

          {displayWidgets.map((widget) => (
            <div key={widget.id} className={styles.tableRow}>
              <div className={styles.widgetName} onClick={() => navigate(`/widgets/${widget.id}`)}>{widget.name}</div>
              <div>
                {canManage ? (
                  <button
                    className={`${styles.statusBadge} ${widget.isActive ? styles.statusEnabled : styles.statusDisabled}`}
                    onClick={() => toggleWidget(widget.id)}
                  >
                    {widget.isActive ? (
                      <>
                        <CheckCircle size={14} color="#065f46" />
                        Active
                      </>
                    ) : (
                      <>
                        <XCircle size={14} color="#991b1b" />
                        Inactive
                      </>
                    )}
                  </button>
                ) : (
                  <span className={`${styles.statusBadge} ${styles.statusEnabled}`}>
                    <CheckCircle size={14} color="#065f46" />
                    Active
                  </span>
                )}
              </div>
              <div className={styles.portalCell}>
                <a
                  href={getPortalUrl(widget)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.portalLink}
                  title={getPortalUrl(widget)}
                >
                  <ExternalLink size={12} />
                  /portal/{widget.id.slice(0, 8)}...
                </a>
                <button
                  className={styles.portalCopyBtn}
                  onClick={() => copyPortalLink(widget)}
                  title="Copy portal link"
                  aria-label={`Copy portal link for ${widget.name}`}
                >
                  <Copy size={12} />
                </button>
              </div>
              <div className={styles.updated}>
                {new Date(widget.createdAt).toLocaleDateString()}
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.actionBtn}
                  onClick={() => navigate(`/widgets/${widget.id}`)}
                  title="Edit"
                  aria-label={`Edit ${widget.name}`}
                >
                  <Pencil size={16} />
                </button>
                {canManage && (
                  <button
                    className={styles.actionBtn}
                    onClick={() => setDuplicateTarget(widget)}
                    title="Duplicate"
                    aria-label={`Duplicate ${widget.name}`}
                  >
                    <Copy size={16} />
                  </button>
                )}
                {canDelete && (
                  <button
                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                    onClick={() => setDeleteTarget(widget)}
                    title="Delete"
                    aria-label={`Delete ${widget.name}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <InputModal
        open={!!duplicateTarget}
        title="Duplicate AI Agent"
        label="Agent Name"
        initialValue={duplicateTarget?.name}
        placeholder="Enter new agent name"
        confirmText="Duplicate"
        cancelText="Cancel"
        loading={duplicating}
        loadingText="Duplicating..."
        onConfirm={handleConfirmDuplicate}
        onCancel={() => {
          if (!duplicating) {
            setDuplicateTarget(null);
          }
        }}
      />

      <InputModal
        open={!!deleteTarget}
        title="Delete AI Agent"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        showInput={false}
        loading={deleting}
        loadingText="Deleting..."
        onConfirm={handleDelete}
        onCancel={() => {
          if (!deleting) {
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}
